import React, { FC, useCallback, useMemo, useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, message } from 'antd';
import { connect, useSelector } from 'react-redux';
import { nullSafeGet, getCurrency } from '../../utils/DataAccessUtils';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { toolsRestCrudThunksForSupplier } from '../../thunks/tools_thunks';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';

const FORM_NAME = 'add-tool-form';

const AddToolModal: FC<any> = ({
	visible,
	onCancel,
	onSuccess,
	tool,
	createTool,
	updateTool,
	stockLocations,
	fetchStockLocations,
}): React.ReactElement => {
	const currentUser = useSelector((state: any) => state.session.currentUser);
	const [saving, setSaving] = useState(false);

	const currency = useMemo(() => getCurrency({ currentUser }), [currentUser]);

	const isEdit = useMemo(() => !!nullSafeGet('id', tool), [tool]);

	const initialValues = useMemo(
		() => ({
			name: nullSafeGet('name', tool) || undefined,
			description: nullSafeGet('description', tool) || undefined,
			costPerUse: nullSafeGet('costPerUse', tool) || undefined,
			stockLocationId:
				nullSafeGet('stockLocationId', tool) || nullSafeGet('stockLocation.id', tool) || undefined,
		}),
		[tool]
	);

	const onSubmit = useCallback(
		(values) => {
			const submitMethod = isEdit ? updateTool : createTool;
			const entity = isEdit ? { ...values, id: tool.id } : values;
			setSaving(true);
			submitMethod(entity)
				.then(() => {
					onSuccess();
				})
				.catch((err) => message.error(err))
				.finally(() => setSaving(false));
		},
		[isEdit, tool, createTool, updateTool, onSuccess]
	);

	return (
		<Modal
			visible={visible}
			width={600}
			title={isEdit ? 'Edit Tool' : 'Add Tool'}
			onCancel={onCancel}
			closable={true}
			footer={[
				<Button key="cancel" onClick={onCancel} size="large">
					Cancel
				</Button>,
				<Button
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_NAME}
					loading={saving}
				>
					{isEdit ? 'Save' : 'Add Tool'}
				</Button>,
			]}
		>
			<Form
				id={FORM_NAME}
				layout="vertical"
				requiredMark={false}
				initialValues={initialValues}
				onFinish={onSubmit}
			>
				<Form.Item
					name="name"
					label="Name"
					rules={[
						{
							required: true,
							message: 'Please enter a tool name',
						},
					]}
				>
					<Input placeholder="example" />
				</Form.Item>
				<Form.Item name="description" label="Description">
					<Input.TextArea placeholder="Add a note..." autoSize={{ minRows: 3 }} />
				</Form.Item>
				<Form.Item
					name="costPerUse"
					label="Cost Per Use"
					rules={[
						{
							required: true,
							message: 'Please enter cost per use',
						},
					]}
				>
					<InputNumber
						style={{ width: '100%' }}
						placeholder="0.00"
						min={0}
						precision={2}
						addonAfter={nullSafeGet('id', currency) || 'USD'}
					/>
				</Form.Item>
				<Form.Item
					name="stockLocationId"
					label="Stock Location"
					rules={[
						{
							required: true,
							message: 'Please select a stock location',
						},
					]}
				>
					<OWAsyncSelect
						stateSlice={stockLocations}
						targetCollectionName="stockLocationDropdown"
						valueAccessor={(el) => el.id}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							fetchStockLocations(
								{ search: searchText },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							);
						}}
						renderRecord={(stockLocation) => (
							<Select.Option key={stockLocation.id} value={stockLocation.id}>
								{stockLocation.name}
							</Select.Option>
						)}
						fetchMultiple={() => {}}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	stockLocations: state.stock_locations,
});

const mapDispatchToProps = (dispatch) => ({
	createTool: (entity) => dispatch(toolsRestCrudThunksForSupplier.create(entity)),
	updateTool: (entity) => dispatch(toolsRestCrudThunksForSupplier.update(entity)),
	fetchStockLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddToolModal);
